import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Activity, 
  Calendar, 
  Clock, 
  Users, 
  ChevronRight,
  Plus,
  Trophy,
  Target,
  TrendingUp,
  BarChart3
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import api from '@/services/api';
import { format } from 'date-fns';

interface Session {
  id: string;
  title: string;
  date: string;
  type: 'Practice' | 'Match' | 'Training' | 'Assessment';
  status: 'Scheduled' | 'InProgress' | 'Completed' | 'Cancelled';
  coach: {
    id: string;
    name: string;
    email: string;
  };
  players: Array<{
    id: string;
    name: string;
  }>;
  duration?: number;
  notes?: string;
  _count?: {
    shotData: number;
  };
  metadata?: any;
}

export function SessionsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState<Session[]>([]);
  const [activeTab, setActiveTab] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSessions();
  }, [activeTab]);

  const fetchSessions = async () => {
    try {
      setLoading(true);
      const params: any = {
        page: 1,
        limit: 20,
        sortBy: 'date',
        sortOrder: 'desc'
      };

      // Filter by status based on active tab
      if (activeTab !== 'all') {
        params.status = activeTab;
      }

      const response = await api.get('/sessions', { params });
      setSessions(response.data.sessions);
    } catch (error) {
      console.error('Failed to fetch sessions:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'InProgress':
        return 'bg-green-500';
      case 'Completed':
        return 'bg-blue-500';
      case 'Scheduled':
        return 'bg-yellow-500';
      case 'Cancelled':
        return 'bg-red-500';
      default:
        return 'bg-gray-500';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'Practice':
        return <Activity className="h-4 w-4" />;
      case 'Match':
        return <Trophy className="h-4 w-4" />;
      case 'Training':
        return <Target className="h-4 w-4" />;
      case 'Assessment':
        return <BarChart3 className="h-4 w-4" />;
      default:
        return <Activity className="h-4 w-4" />;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold">Training Sessions</h1>
              <p className="text-sm text-muted-foreground">
                Manage and review cricket training sessions
              </p>
            </div>
            <div className="flex gap-2">
              <Button onClick={() => navigate('/dashboard')}>
                Back to Dashboard
              </Button>
              {['COACH', 'ADMIN'].includes(user?.role || '') && (
                <Button onClick={() => navigate('/sessions/new')}>
                  <Plus className="mr-2 h-4 w-4" />
                  New Session
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="all">All Sessions</TabsTrigger>
            <TabsTrigger value="InProgress">In Progress</TabsTrigger>
            <TabsTrigger value="Completed">Completed</TabsTrigger>
            <TabsTrigger value="Scheduled">Scheduled</TabsTrigger>
          </TabsList>

          <TabsContent value={activeTab}>
            {loading ? (
              <div className="text-center py-8">Loading sessions...</div>
            ) : sessions.length === 0 ? (
              <Card>
                <CardContent className="text-center py-8">
                  <p className="text-muted-foreground">No sessions found</p>
                  {['COACH', 'ADMIN'].includes(user?.role || '') && (
                    <Button 
                      className="mt-4"
                      onClick={() => navigate('/sessions/new')}
                    >
                      <Plus className="mr-2 h-4 w-4" />
                      Create First Session
                    </Button>
                  )}
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {sessions.map((session) => (
                  <Card 
                    key={session.id}
                    className="hover:shadow-lg transition-shadow cursor-pointer"
                    onClick={() => navigate(`/sessions/${session.id}`)}
                  >
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            {getTypeIcon(session.type)}
                            <CardTitle>{session.title}</CardTitle>
                          </div>
                          <CardDescription>
                            <div className="flex items-center gap-4 mt-2">
                              <span className="flex items-center gap-1">
                                <Calendar className="h-3 w-3" />
                                {format(new Date(session.date), 'PPP')}
                              </span>
                              {session.duration && (
                                <span className="flex items-center gap-1">
                                  <Clock className="h-3 w-3" />
                                  {session.duration} min
                                </span>
                              )}
                              <span className="flex items-center gap-1">
                                <Users className="h-3 w-3" />
                                {session.players.length} players
                              </span>
                            </div>
                          </CardDescription>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className={getStatusColor(session.status)}>
                            {session.status}
                          </Badge>
                          <ChevronRight className="h-5 w-5 text-muted-foreground" />
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-muted-foreground">Coach</p>
                          <p className="font-medium">{session.coach.name}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Shots Recorded</p>
                          <p className="font-medium">{session._count?.shotData || 0}</p>
                        </div>
                      </div>
                      {session.notes && (
                        <p className="text-sm text-muted-foreground mt-3 line-clamp-2">
                          {session.notes}
                        </p>
                      )}
                      {session.metadata?.summary && (
                        <div className="flex items-center gap-4 mt-3 text-sm">
                          <span>
                            <TrendingUp className="inline h-3 w-3 mr-1" />
                            Avg Distance: {session.metadata.summary.averageDistance?.toFixed(1)}m
                          </span>
                          <span>
                            <Activity className="inline h-3 w-3 mr-1" />
                            Max Speed: {session.metadata.summary.maxExitVelocity?.toFixed(1)} m/s
                          </span>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}